import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth, fetchBlobWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

// ============================================================================
// Types
// ============================================================================

// Los enums se importan desde el archivo generado por `openapi-typescript`
// (`src/lib/api/generated.d.ts`) que se regenera desde el OpenAPI del backend.
// Para regenerar tras un cambio en el backend:
//   npm run generate:api-types:full
// (o, si ya tienes /tmp/licitai-openapi.json, basta con `npm run generate:api-types`).
//
// El test de contrato `backend/tests/test_enums_contract.py` falla en CI si
// alguien añade/renombra un valor de enum sin actualizar este archivo.
import type { components } from "@/lib/api/generated";

export type ProposalType = components["schemas"]["ProposalType"];
export type ProposalStatus = components["schemas"]["ProposalStatus"];
export type SectionType = components["schemas"]["SectionType"];
export type TemplateType = components["schemas"]["TemplateType"];

// Sprint 4: tipos del loop crítico-escritor (vienen del OpenAPI generado)
export type CriticDimension = components["schemas"]["CriticDimension"];
export type CriticIteration = components["schemas"]["CriticIteration"];

// Mejora M (changelog cliente): evaluación manual de sección.
export type ManualEvaluation = components["schemas"]["ManualEvaluation"];
export type SectionApplySuggestionsResponse =
  components["schemas"]["SectionApplySuggestionsResponse"];
// Mejora "humanizar": reescritura de estilo con preview (sin persistir).
export type SectionHumanizeResponse =
  components["schemas"]["SectionHumanizeResponse"];
export type RequirementsCoverageResult =
  components["schemas"]["RequirementsCoverageResult"];
export type RequirementCoverage =
  components["schemas"]["RequirementCoverage"];
export type RequirementsCoverageSummary =
  components["schemas"]["RequirementsCoverageSummary"];
// Feature C: requisitos del pliego asignados a secciones (persistidos, editables)
export type RequirementResponse =
  components["schemas"]["RequirementResponse"];
export type RequirementCreate =
  components["schemas"]["RequirementCreate"];
export type RequirementUpdate =
  components["schemas"]["RequirementUpdate"];
export type RequirementsExtractionResult =
  components["schemas"]["RequirementsExtractionResult"];

export interface ProposalStats {
  total_words: number;
  total_sections: number;
  sections_completed: number;
  sections_ai_generated: number;
  completion_percentage: number;
}

export interface ProposalListItem {
  id: string;
  title: string;
  proposal_type: ProposalType;
  status: ProposalStatus;
  licitacion_id: string;
  licitacion_title?: string;
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  // Nombre legible del creador (resuelto en backend) para la tarjeta.
  created_by_name?: string | null;
  assigned_to?: string;
  due_date?: string;
  stats?: ProposalStats;
}

/**
 * Estado de la generación IA asíncrona de una sección (202 + polling).
 * El frontend sondea `generation_status` hasta COMPLETADO/ERROR.
 */
export type SectionGenerationStatus = "IDLE" | "GENERANDO" | "COMPLETADO" | "ERROR";

export interface Section {
  id: string;
  proposal_id: string;
  title: string;
  content_html: string;
  content_plain: string;
  section_order: number;
  section_type: SectionType;
  ai_generated: boolean;
  ai_prompt_used?: string;
  word_count: number;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
  // Generación IA asíncrona: la sección se genera en segundo plano y el
  // frontend sondea este estado. GENERANDO → spinner; COMPLETADO → refresca
  // el contenido; ERROR → aviso con `generation_error`.
  generation_status?: SectionGenerationStatus;
  generation_error?: string | null;
  // Sprint 4: historial del loop crítico-escritor (poblado por el backend
  // desde extra_metadata cuando critic_enabled=true).
  critic_iterations?: CriticIteration[] | null;
  final_quality_score?: number | null;
  // Mejora M (changelog cliente): última evaluación manual + flag stale.
  last_manual_evaluation?: ManualEvaluation | null;
  manual_evaluation_stale?: boolean;
  // Mejora "Subsecciones de primera clase": jerarquía padre→hijo.
  // `parent_section_id=null` y `depth=0` para secciones raíz; valor +
  // `depth=1` para subsecciones. El sidebar las agrupa visualmente bajo
  // su padre y la generación IA pasa contexto del padre.
  parent_section_id?: string | null;
  depth?: number;
}

export interface Proposal {
  id: string;
  licitacion_id: string;
  title: string;
  proposal_type: ProposalType;
  status: ProposalStatus;
  template_id?: string;
  version: number;
  created_by: string;
  assigned_to?: string;
  due_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  sections: Section[];
  stats?: ProposalStats;
}

export interface ProposalVersion {
  id: string;
  proposal_id: string;
  version_number: number;
  change_summary?: string;
  created_by: string;
  created_at: string;
}

export interface VersionSnapshotSection {
  id: string;
  title: string;
  content_html: string;
  section_order: number;
}

export interface VersionSnapshotData {
  title: string;
  status: string;
  sections: VersionSnapshotSection[];
}

export interface ProposalVersionDetail extends ProposalVersion {
  snapshot_data: VersionSnapshotData;
}

export interface TemplateSectionConfig {
  title: string;
  section_type: SectionType;
  section_order: number;
  ai_prompt?: string;
  suggested_word_count?: number;
  context_from_previous?: boolean;
  // Mejora "Subsecciones de primera clase": el wizard manda el árbol
  // que confirmó el usuario. El backend persiste los hijos con
  // parent_section_id y depth=1.
  subsections?: TemplateSectionConfig[];
}

export interface ProposalTemplate {
  id: string;
  name: string;
  description?: string;
  template_type: TemplateType;
  sections_config: TemplateSectionConfig[];
  file_path?: string;
  is_default: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateProposalParams {
  licitacion_id: string;
  template_id?: string;
  proposal_type: ProposalType;
  title?: string;
  // Mejora "Sugerir estructura" del changelog cliente: si viene, se
  // usan estas secciones (en lugar del template o las default).
  sections_config?: TemplateSectionConfig[];
}

// Mejora "Sugerir estructura": types del endpoint suggest-structure.
export interface SuggestedSection {
  title: string;
  section_type: string;
  order: number;
  rationale?: string | null;
  // Mejora "Subsecciones de primera clase": cada sección de calado
  // viene con sus apartados internos. Máximo 1 nivel de anidamiento.
  subsections?: SuggestedSection[];
}

export interface SuggestStructureResponse {
  sections: SuggestedSection[];
  notice?: string | null;
}

export interface UpdateProposalParams {
  title?: string;
  status?: ProposalStatus;
  assigned_to?: string;
  due_date?: string;
  notes?: string;
}

export interface UpdateSectionParams {
  // Ambos opcionales: edición de contenido (content_html) o renombrado
  // del índice (title). Debe ir al menos uno.
  content_html?: string;
  title?: string;
}

export interface GenerateSectionParams {
  instructions?: string;
  // Sprint 4: flags del loop crítico-escritor (omitidos = defaults del backend)
  critic_enabled?: boolean;
  max_iterations?: number;
  quality_threshold?: number;
  // Mejora A (changelog cliente): longitud objetivo de la sección generada.
  target_words?: number;
}

export interface RegenerateSectionParams {
  feedback: string;
}

// Mejora B (changelog cliente): resumir a N palabras.
export interface ResumeSectionParams {
  target_words: number;
  preserve_pliego_requirements?: boolean;
  instructions?: string;
}

export interface ReorderSectionsParams {
  section_ids: string[];
}

export interface CreateVersionParams {
  change_summary: string;
}

export interface CreateSectionParams {
  title: string;
  section_type?: SectionType;
  section_order: number;
  content_html?: string;
  /**
   * Si viene, la sección se crea como SUBSECCIÓN de esa sección madre.
   * La madre debe ser una sección raíz (el modelo admite 2 niveles).
   */
  parent_section_id?: string | null;
}

// ============================================================================
// API Functions
// ============================================================================

// ==================== PROPUESTAS ====================

async function fetchProposals(licitacionId?: string): Promise<ProposalListItem[]> {
  const params = licitacionId ? `?licitacion_id=${licitacionId}` : "";
  return fetchWithAuth(`/api/v1/proposals${params}`);
}

async function fetchProposal(proposalId: string): Promise<Proposal> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}`);
}

async function createProposal(params: CreateProposalParams): Promise<Proposal> {
  return fetchWithAuth("/api/v1/proposals", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

async function updateProposal(proposalId: string, params: UpdateProposalParams): Promise<Proposal> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}`, {
    method: "PUT",
    body: JSON.stringify(params),
  });
}

async function deleteProposal(proposalId: string): Promise<void> {
  await fetchWithAuth(`/api/v1/proposals/${proposalId}`, {
    method: "DELETE",
  });
}

// ==================== SECCIONES ====================

async function updateSection(
  proposalId: string,
  sectionId: string,
  params: UpdateSectionParams
): Promise<Section> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}/sections/${sectionId}`, {
    method: "PUT",
    body: JSON.stringify(params),
  });
}

async function generateSection(
  proposalId: string,
  sectionId: string,
  params: GenerateSectionParams
): Promise<Section> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}/sections/${sectionId}/generate`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

async function regenerateSection(
  proposalId: string,
  sectionId: string,
  params: RegenerateSectionParams
): Promise<Section> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}/sections/${sectionId}/regenerate`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

async function resumeSection(
  proposalId: string,
  sectionId: string,
  params: ResumeSectionParams
): Promise<Section> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}/sections/${sectionId}/resume`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// Mejora M (changelog cliente): evaluar y aplicar sugerencias a una sección.
async function evaluateSection(
  proposalId: string,
  sectionId: string
): Promise<ManualEvaluation> {
  return fetchWithAuth(
    `/api/v1/proposals/${proposalId}/sections/${sectionId}/evaluate`,
    { method: "POST", body: JSON.stringify({}) }
  );
}

// Human-in-the-loop: sugerencia seleccionada (y quizá editada) por el usuario.
export interface SelectedSuggestion {
  dimension: string;
  text: string;
}

async function applySectionSuggestions(
  proposalId: string,
  sectionId: string,
  params: {
    evaluation_id?: string;
    // Si se omite, el backend aplica todas (retrocompatible). Si es una lista,
    // aplica SOLO esas. Lista vacía → el backend devuelve error.
    selected_suggestions?: SelectedSuggestion[];
  }
): Promise<SectionApplySuggestionsResponse> {
  return fetchWithAuth(
    `/api/v1/proposals/${proposalId}/sections/${sectionId}/apply-suggestions`,
    { method: "POST", body: JSON.stringify(params) }
  );
}

// Mejora "humanizar": reescribe el ESTILO de la sección (ritmo variado, prosa,
// sin muletillas de IA) conservando datos y citas. Devuelve preview; el caller
// muestra un diff y decide si guarda.
async function humanizeSection(
  proposalId: string,
  sectionId: string,
  params: { instructions?: string }
): Promise<SectionHumanizeResponse> {
  return fetchWithAuth(
    `/api/v1/proposals/${proposalId}/sections/${sectionId}/humanize`,
    { method: "POST", body: JSON.stringify(params) }
  );
}

async function fetchRequirementsCoverage(
  proposalId: string
): Promise<RequirementsCoverageResult> {
  return fetchWithAuth(
    `/api/v1/proposals/${proposalId}/requirements-coverage`,
    { method: "POST", body: JSON.stringify({}) }
  );
}

async function reorderSections(
  proposalId: string,
  params: ReorderSectionsParams
): Promise<Section[]> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}/sections/reorder`, {
    method: "PUT",
    body: JSON.stringify(params),
  });
}

async function createSection(
  proposalId: string,
  params: CreateSectionParams
): Promise<Section> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}/sections`, {
    method: "POST",
    body: JSON.stringify({
      ...params,
      section_type: params.section_type || "CUSTOM",
      content_html: params.content_html || "",
    }),
  });
}

async function deleteSection(proposalId: string, sectionId: string): Promise<void> {
  await fetchWithAuth(`/api/v1/proposals/${proposalId}/sections/${sectionId}`, {
    method: "DELETE",
  });
}

// Mejora "Importar desde Word/PDF": sube un .docx o .pdf y vuelca su
// contenido en la sección. fetchWithAuth detecta el FormData y omite
// Content-Type.
async function importSectionFromFile(
  proposalId: string,
  sectionId: string,
  file: File
): Promise<Section> {
  const formData = new FormData();
  formData.append("file", file);
  return fetchWithAuth(
    `/api/v1/proposals/${proposalId}/sections/${sectionId}/import-from-file`,
    { method: "POST", body: formData }
  );
}

// ==================== VERSIONES ====================

async function fetchProposalVersions(proposalId: string): Promise<ProposalVersion[]> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}/versions`);
}

async function fetchProposalVersion(
  proposalId: string,
  versionId: string
): Promise<ProposalVersionDetail> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}/versions/${versionId}`);
}

async function createVersion(
  proposalId: string,
  params: CreateVersionParams
): Promise<ProposalVersion> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}/versions`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

async function restoreVersion(proposalId: string, versionId: string): Promise<Proposal> {
  return fetchWithAuth(`/api/v1/proposals/${proposalId}/versions/${versionId}/restore`, {
    method: "POST",
  });
}

// ==================== EXPORTACIÓN ====================

async function exportProposal(proposalId: string, format: "docx" | "pdf"): Promise<Blob> {
  return fetchBlobWithAuth(`/api/v1/proposals/${proposalId}/export/${format}`);
}

// ==================== TEMPLATES ====================

// Mejoras L/I (changelog cliente): editor de templates en UI.
export interface CreateProposalTemplateParams {
  name: string;
  description?: string;
  template_type: TemplateType;
  sections_config: TemplateSectionConfig[];
  is_default?: boolean;
}

export interface UpdateProposalTemplateParams {
  name?: string;
  description?: string;
  sections_config?: TemplateSectionConfig[];
  is_default?: boolean;
}

async function fetchProposalTemplates(): Promise<ProposalTemplate[]> {
  return fetchWithAuth("/api/v1/proposals/templates");
}

async function createProposalTemplate(
  params: CreateProposalTemplateParams
): Promise<ProposalTemplate> {
  return fetchWithAuth("/api/v1/proposals/templates", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

async function updateProposalTemplate(
  templateId: string,
  params: UpdateProposalTemplateParams
): Promise<ProposalTemplate> {
  return fetchWithAuth(`/api/v1/proposals/templates/${templateId}`, {
    method: "PUT",
    body: JSON.stringify(params),
  });
}

async function deleteProposalTemplate(templateId: string): Promise<void> {
  await fetchWithAuth(`/api/v1/proposals/templates/${templateId}`, {
    method: "DELETE",
  });
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook para obtener la lista de propuestas de una licitación
 * @param licitacionId - ID de la licitación (opcional)
 */
export function useProposals(licitacionId?: string) {
  return useQuery<ProposalListItem[], Error>({
    queryKey: ["proposals", licitacionId],
    queryFn: () => fetchProposals(licitacionId),
    enabled: !!licitacionId,
  });
}

/**
 * Hook para obtener el detalle completo de una propuesta con secciones
 * @param proposalId - ID de la propuesta
 */
export function useProposal(proposalId: string | null) {
  return useQuery<Proposal, Error>({
    queryKey: ["proposal", proposalId],
    queryFn: () => fetchProposal(proposalId!),
    enabled: !!proposalId,
    // Generación asíncrona: mientras haya alguna sección GENERANDO, sondeamos
    // cada 2,5s para reflejar el avance y detectar COMPLETADO/ERROR. Cuando
    // ninguna está generando, `false` desactiva el polling (sin coste ocioso).
    refetchInterval: (query) => {
      const anyGenerating = query.state.data?.sections?.some(
        (s) => s.generation_status === "GENERANDO"
      );
      return anyGenerating ? 2500 : false;
    },
  });
}

/**
 * Hook para crear una nueva propuesta
 */
export function useSuggestStructure() {
  return useMutation<
    SuggestStructureResponse,
    Error,
    { licitacion_id: string; proposal_type: ProposalType }
  >({
    mutationFn: (payload) =>
      fetchWithAuth("/api/v1/proposals/suggest-structure", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
  });
}

export function useCreateProposal() {
  const queryClient = useQueryClient();

  return useMutation<Proposal, Error, CreateProposalParams>({
    mutationFn: createProposal,
    onSuccess: (data) => {
      // Invalidar la lista de propuestas para la licitación
      queryClient.invalidateQueries({
        queryKey: ["proposals", data.licitacion_id],
      });
      showToast.success("Propuesta creada correctamente");
    },
    onError: () => {
      showToast.error("Error al crear la propuesta");
    },
  });
}

/**
 * Hook para actualizar los metadatos de una propuesta
 */
export function useUpdateProposal(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<Proposal, Error, UpdateProposalParams>({
    mutationFn: (params) => updateProposal(proposalId, params),
    onSuccess: (data) => {
      // Invalidar tanto el detalle como la lista
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      queryClient.invalidateQueries({ queryKey: ["proposals", data.licitacion_id] });
      showToast.success("Propuesta actualizada");
    },
    onError: () => {
      showToast.error("Error al actualizar la propuesta");
    },
  });
}

/**
 * Variante de update no ligada a un proposalId fijo: recibe el id en cada
 * mutación. Útil para listas (la tarjeta de propuesta) donde no se puede
 * llamar a un hook por cada elemento del map.
 */
export function useUpdateProposalById() {
  const queryClient = useQueryClient();

  return useMutation<Proposal, Error, { proposalId: string; data: UpdateProposalParams }>(
    {
      mutationFn: ({ proposalId, data }) => updateProposal(proposalId, data),
      onSuccess: (data, vars) => {
        queryClient.invalidateQueries({ queryKey: ["proposal", vars.proposalId] });
        queryClient.invalidateQueries({
          queryKey: ["proposals", data.licitacion_id],
        });
        showToast.success("Propuesta actualizada");
      },
      onError: () => {
        showToast.error("Error al actualizar la propuesta");
      },
    }
  );
}

/**
 * Hook para eliminar una propuesta
 */
export function useDeleteProposal() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { proposalId: string; licitacionId: string }>({
    mutationFn: ({ proposalId }) => deleteProposal(proposalId),
    onSuccess: (_, variables) => {
      // Invalidar la lista de propuestas
      queryClient.invalidateQueries({
        queryKey: ["proposals", variables.licitacionId],
      });
      showToast.success("Propuesta eliminada");
    },
    onError: () => {
      showToast.error("Error al eliminar la propuesta");
    },
  });
}

/**
 * Hook para actualizar el contenido de una sección (edición manual)
 *
 * No emite toast de éxito porque el componente padre maneja el indicador
 * de auto-save (`AutoSaveIndicator`); un toast por cada PUT sería ruidoso.
 */
export function useUpdateSection(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<Section, Error, { sectionId: string } & UpdateSectionParams>({
    mutationFn: ({ sectionId, ...params }) => updateSection(proposalId, sectionId, params),
    onSuccess: () => {
      // Invalidar el detalle de la propuesta para refrescar secciones
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
    },
    onError: () => {
      showToast.error("Error al actualizar la sección");
    },
  });
}

/**
 * Hook para generar contenido de una sección con IA
 */
export function useGenerateSection(proposalId: string) {
  const queryClient = useQueryClient();

  // Generación ASÍNCRONA: la mutación solo ARRANCA la generación (el backend
  // responde 202 con la sección en GENERANDO). El resultado no llega aquí, sino
  // vía el polling de `useProposal`; el toast de éxito/error final lo emite la
  // página al detectar la transición GENERANDO→COMPLETADO/ERROR. Aquí solo
  // avisamos del arranque y tratamos el error de ARRANQUE (404 / red).
  return useMutation<Section, Error, { sectionId: string } & GenerateSectionParams>({
    mutationFn: ({ sectionId, ...params }) => generateSection(proposalId, sectionId, params),
    onSuccess: () => {
      // La sección pasa a GENERANDO → invalidar activa el polling.
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      showToast.info("Generando la sección con IA… puede tardar un par de minutos.");
    },
    onError: () => {
      showToast.error("No se pudo iniciar la generación de la sección");
    },
  });
}

/**
 * Hook para regenerar una sección con feedback del usuario
 */
export function useRegenerateSection(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<Section, Error, { sectionId: string } & RegenerateSectionParams>({
    mutationFn: ({ sectionId, ...params }) => regenerateSection(proposalId, sectionId, params),
    onSuccess: () => {
      // Invalidar el detalle de la propuesta
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      showToast.success("Sección regenerada con IA");
    },
    onError: () => {
      showToast.error("Error al regenerar la sección");
    },
  });
}

/**
 * Mejora B (changelog cliente): hook para resumir una sección a N palabras
 * preservando los requisitos del pliego.
 */
export function useResumeSection(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<Section, Error, { sectionId: string } & ResumeSectionParams>({
    mutationFn: ({ sectionId, ...params }) => resumeSection(proposalId, sectionId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      showToast.success("Sección resumida");
    },
    onError: (error) => {
      showToast.error(error.message || "Error al resumir la sección");
    },
  });
}

/**
 * Mejora M (changelog cliente): evaluar una sección ya escrita contra el
 * pliego (puede ser un borrador manual o una versión editada tras la
 * generación IA). Persiste la evaluación en extra_metadata para histórico.
 */
export function useEvaluateSection(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<ManualEvaluation, Error, { sectionId: string }>({
    mutationFn: ({ sectionId }) => evaluateSection(proposalId, sectionId),
    onSuccess: () => {
      // Refrescamos la propuesta para que pinte last_manual_evaluation
      // (que llega serializado en SectionResponse desde extra_metadata).
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      showToast.success("Sección evaluada");
    },
    onError: (error) => {
      showToast.error(error.message || "Error al evaluar la sección");
    },
  });
}

/**
 * Mejora M (changelog cliente): aplicar las sugerencias del crítico a la
 * sección. Devuelve la versión refinada para previsualización; el caller
 * decide si la guarda con `useUpdateSection`.
 */
export function useApplySectionSuggestions(proposalId: string) {
  return useMutation<
    SectionApplySuggestionsResponse,
    Error,
    {
      sectionId: string;
      evaluation_id?: string;
      selected_suggestions?: SelectedSuggestion[];
    }
  >({
    mutationFn: ({ sectionId, evaluation_id, selected_suggestions }) =>
      applySectionSuggestions(proposalId, sectionId, {
        evaluation_id,
        selected_suggestions,
      }),
    onError: (error) => {
      showToast.error(error.message || "Error al aplicar las sugerencias");
    },
  });
}

/**
 * Mejora "humanizar": reescribe el estilo de la sección para que suene a
 * redactor humano. Devuelve la versión humanizada para previsualización
 * (diff); el caller decide si la guarda con el flujo normal de auto-save.
 */
export function useHumanizeSection(proposalId: string) {
  return useMutation<
    SectionHumanizeResponse,
    Error,
    { sectionId: string; instructions?: string }
  >({
    mutationFn: ({ sectionId, instructions }) =>
      humanizeSection(proposalId, sectionId, { instructions }),
    onError: (error) => {
      showToast.error(error.message || "Error al humanizar la sección");
    },
  });
}

/**
 * Mejora "B": cobertura de requisitos del pliego a nivel PROPUESTA. Mira la
 * propuesta completa contra el pliego y devuelve una matriz requisito →
 * ¿cubierto? → ¿en qué sección(es)?. Resuelve los falsos huecos del crítico
 * por sección (que juzga cada sección en aislamiento). Cuesta tokens, así que
 * no auto-invalida cache: el usuario relanza con el botón.
 */
export function useRequirementsCoverage(proposalId: string) {
  return useMutation<RequirementsCoverageResult, Error, void>({
    mutationFn: () => fetchRequirementsCoverage(proposalId),
    onError: (error) => {
      showToast.error(
        error.message || "Error al analizar la cobertura del pliego"
      );
    },
  });
}

/**
 * Hook para reordenar las secciones de una propuesta
 */
export function useReorderSections(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<Section[], Error, ReorderSectionsParams>({
    mutationFn: (params) => reorderSections(proposalId, params),
    onSuccess: () => {
      // Invalidar el detalle de la propuesta
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      showToast.success("Secciones reordenadas");
    },
    onError: () => {
      showToast.error("Error al reordenar las secciones");
    },
  });
}

/**
 * Extrae el índice de un .docx para crear una propuesta con esa estructura.
 *
 * Solo LEE el fichero: no crea ni guarda nada. Devuelve la misma forma que
 * `useSuggestStructure`, así que el diálogo de revisión es el mismo.
 */
export function useParseWordIndex() {
  return useMutation<SuggestStructureResponse, Error, File>({
    mutationFn: (file) => {
      const formData = new FormData();
      formData.append("file", file);
      // fetchWithAuth detecta el FormData y omite el Content-Type.
      return fetchWithAuth("/api/v1/proposals/parse-word-index", {
        method: "POST",
        body: formData,
      });
    },
    onError: (error) => {
      showToast.error(error.message || "No se pudo leer el índice del documento");
    },
  });
}

/**
 * Hook para crear una nueva sección custom
 */
export function useCreateSection(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<Section, Error, CreateSectionParams>({
    mutationFn: (params) => createSection(proposalId, params),
    onSuccess: () => {
      // Invalidar el detalle de la propuesta
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      showToast.success("Sección creada");
    },
    onError: () => {
      showToast.error("Error al crear la sección");
    },
  });
}

/**
 * Hook para eliminar una sección
 */
export function useDeleteSection(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { sectionId: string }>({
    mutationFn: ({ sectionId }) => deleteSection(proposalId, sectionId),
    onSuccess: () => {
      // Invalidar el detalle de la propuesta
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      showToast.success("Sección eliminada");
    },
    onError: () => {
      showToast.error("Error al eliminar la sección");
    },
  });
}

/**
 * Hook para importar el contenido de una sección desde un fichero Word o PDF.
 */
export function useImportSectionFromFile(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<Section, Error, { sectionId: string; file: File }>({
    mutationFn: ({ sectionId, file }) =>
      importSectionFromFile(proposalId, sectionId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      showToast.success("Contenido importado");
    },
    onError: (error) => {
      showToast.error(error.message || "No se pudo importar el archivo");
    },
  });
}

/**
 * Hook para obtener las versiones de una propuesta
 */
export function useProposalVersions(proposalId: string | null) {
  return useQuery<ProposalVersion[], Error>({
    queryKey: ["proposal-versions", proposalId],
    queryFn: () => fetchProposalVersions(proposalId!),
    enabled: !!proposalId,
  });
}

/**
 * Hook para obtener el detalle (con snapshot_data) de una versión específica.
 */
export function useProposalVersion(proposalId: string | null, versionId: string | null) {
  return useQuery<ProposalVersionDetail, Error>({
    queryKey: ["proposal-version", proposalId, versionId],
    queryFn: () => fetchProposalVersion(proposalId!, versionId!),
    enabled: !!proposalId && !!versionId,
  });
}

/**
 * Hook para crear un snapshot (versión) de la propuesta
 */
export function useCreateVersion(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<ProposalVersion, Error, CreateVersionParams>({
    mutationFn: (params) => createVersion(proposalId, params),
    onSuccess: () => {
      // Invalidar la lista de versiones
      queryClient.invalidateQueries({ queryKey: ["proposal-versions", proposalId] });
      // También invalidar el detalle de la propuesta (por si aumenta el número de versión)
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      showToast.success("Versión creada correctamente");
    },
    onError: () => {
      showToast.error("Error al crear la versión");
    },
  });
}

/**
 * Hook para restaurar una versión de la propuesta
 */
export function useRestoreVersion(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation<Proposal, Error, { versionId: string }>({
    mutationFn: ({ versionId }) => restoreVersion(proposalId, versionId),
    onSuccess: () => {
      // Invalidar el detalle de la propuesta
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      showToast.success("Versión restaurada correctamente");
    },
    onError: () => {
      showToast.error("Error al restaurar la versión");
    },
  });
}

/**
 * Hook para exportar una propuesta a DOCX o PDF
 * @param proposalId - ID de la propuesta
 */
export function useExportProposal(proposalId: string) {
  return useMutation<Blob, Error, { format: "docx" | "pdf" }>({
    mutationFn: ({ format }) => exportProposal(proposalId, format),
    onSuccess: (blob, variables) => {
      // Descargar el archivo automáticamente
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `propuesta-${proposalId}.${variables.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast.success("Exportación completada");
    },
    onError: () => {
      showToast.error("Error al exportar la propuesta");
    },
  });
}

/**
 * Hook para obtener la lista de templates de propuestas
 */
export function useProposalTemplates() {
  return useQuery<ProposalTemplate[], Error>({
    queryKey: ["proposal-templates"],
    queryFn: fetchProposalTemplates,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
}

/**
 * Mejoras L/I (changelog cliente): crear, editar y eliminar templates
 * desde la UI sin necesidad de deploy.
 */
export function useCreateProposalTemplate() {
  const queryClient = useQueryClient();
  return useMutation<ProposalTemplate, Error, CreateProposalTemplateParams>({
    mutationFn: createProposalTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal-templates"] });
      showToast.success("Plantilla creada");
    },
    onError: (error) => {
      showToast.error(error.message || "Error al crear la plantilla");
    },
  });
}

export function useUpdateProposalTemplate() {
  const queryClient = useQueryClient();
  return useMutation<
    ProposalTemplate,
    Error,
    { templateId: string } & UpdateProposalTemplateParams
  >({
    mutationFn: ({ templateId, ...params }) =>
      updateProposalTemplate(templateId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal-templates"] });
      showToast.success("Plantilla actualizada");
    },
    onError: (error) => {
      showToast.error(error.message || "Error al actualizar la plantilla");
    },
  });
}

export function useDeleteProposalTemplate() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { templateId: string }>({
    mutationFn: ({ templateId }) => deleteProposalTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal-templates"] });
      showToast.success("Plantilla eliminada");
    },
    onError: (error) => {
      showToast.error(error.message || "Error al eliminar la plantilla");
    },
  });
}

// ==================== REQUISITOS ASIGNADOS A SECCIONES (feature C) ====================

/**
 * Lista los requisitos del pliego asignados a las secciones de una propuesta.
 * El panel del editor los filtra por la sección seleccionada.
 */
export function useProposalRequirements(proposalId: string) {
  return useQuery<RequirementResponse[], Error>({
    queryKey: ["proposal-requirements", proposalId],
    queryFn: () =>
      fetchWithAuth(`/api/v1/proposals/${proposalId}/requirements`),
    enabled: !!proposalId,
  });
}

/**
 * Extrae los requisitos del pliego con IA y los asigna a secciones. Lo dispara
 * el wizard tras crear la propuesta desde estructura sugerida, y un botón
 * "Regenerar". Conserva los requisitos editados a mano (is_manual).
 */
export function useExtractRequirements(proposalId: string) {
  const queryClient = useQueryClient();
  return useMutation<RequirementsExtractionResult, Error, void>({
    mutationFn: () =>
      fetchWithAuth(`/api/v1/proposals/${proposalId}/requirements/extract`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposal-requirements", proposalId],
      });
    },
    onError: (error) => {
      showToast.error(error.message || "Error al extraer los requisitos");
    },
  });
}

export function useCreateRequirement(proposalId: string) {
  const queryClient = useQueryClient();
  return useMutation<RequirementResponse, Error, RequirementCreate>({
    mutationFn: (data) =>
      fetchWithAuth(`/api/v1/proposals/${proposalId}/requirements`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposal-requirements", proposalId],
      });
    },
    onError: (error) => {
      showToast.error(error.message || "Error al añadir el requisito");
    },
  });
}

export function useUpdateRequirement(proposalId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    RequirementResponse,
    Error,
    { requirementId: string; data: RequirementUpdate }
  >({
    mutationFn: ({ requirementId, data }) =>
      fetchWithAuth(
        `/api/v1/proposals/${proposalId}/requirements/${requirementId}`,
        { method: "PATCH", body: JSON.stringify(data) }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposal-requirements", proposalId],
      });
    },
    onError: (error) => {
      showToast.error(error.message || "Error al actualizar el requisito");
    },
  });
}

export function useDeleteRequirement(proposalId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (requirementId) =>
      fetchWithAuth(
        `/api/v1/proposals/${proposalId}/requirements/${requirementId}`,
        { method: "DELETE" }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposal-requirements", proposalId],
      });
    },
    onError: (error) => {
      showToast.error(error.message || "Error al eliminar el requisito");
    },
  });
}
